import React, { useEffect, useRef, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { HomeListResponse } from "@/database";

const donutOptions = {
  title: "",
  width: "100%",
  height: 264,
  colors: ["#4285F4", "#34A853", "#FBBC05", "#EA4335", "#FF6D01"],
  pieHole: 0,
  enableInteractivity: true,
  animation: {
    duration: 1000,
    easing: "out",
    startup: true,
  },
  legend: { position: "bottom" },
  chartArea: { width: "100%", height: "80%" },
};

export const SocialDistribution: React.FC<{
  CHART_SCRIPT_URL: string;
  updateModal: any;
  homeList: HomeListResponse;
}> = ({ CHART_SCRIPT_URL, updateModal, homeList }) => {
  const [showIframe, setShowIframe] = useState<boolean>(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<{
    draw: (data: any, options: any) => void;
  } | null>(null);

  const socialPublishedTypes =
    homeList?.social_embeds_agg?.socialembeds?.social_published_types;

  const drawChart = useCallback(() => {
    if (!window.google?.visualization || !chartRef.current) return;

    let chartData: (string | number)[][] = [["Platform", "Distribution"]];

    if (
      !socialPublishedTypes ||
      !Array.isArray(socialPublishedTypes) ||
      socialPublishedTypes.length === 0
    ) {
      chartData = [
        ["Platform", "Distribution"],
        ["No Data Available", 1],
      ];
    } else {
      socialPublishedTypes.forEach((item) => {
        chartData.push([item.key, item.doc_count]);
      });
    }

    const dataTable = window.google.visualization.arrayToDataTable(chartData);
    const chart = new (window.google.visualization as any).PieChart(
      chartRef.current
    );
    chart.draw(dataTable, donutOptions);
    chartInstance.current = chart;
  }, [socialPublishedTypes]);

  const loadGoogleCharts = useCallback(() => {
    if (!window.google) {
      return;
    }
    window.google.charts.load("current", { packages: ["corechart"] });
    window.google.charts.setOnLoadCallback(() => {
      drawChart();
    });
  }, [drawChart]);

  const addGoogleChartScript = useCallback(() => {
    const script = document.createElement("script");
    script.src = CHART_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      loadGoogleCharts();
    };
    script.onerror = () => {
      console.error("SocialDistribution - Failed to load Google Charts script");
    };
    document.body.appendChild(script);
  }, [CHART_SCRIPT_URL, loadGoogleCharts]);

  useEffect(() => {
    if (!window.google || !window.google.charts) {
      addGoogleChartScript();
    } else {
      loadGoogleCharts();
    }

    const handleResize = () => {
      if (chartInstance.current && chartRef.current) {
        drawChart();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [addGoogleChartScript, loadGoogleCharts, drawChart]);

  const handleCloseIframe = () => {
    setShowIframe(false);
  };

  return (
    <>
      <div className="px-3 pt-3">
        <div className="f-14 fw-medium text-uppercase">
          Social Platform Distribution
        </div>
        <div className="f-10 text-muted fw-italic ">Past 48 hours</div>
      </div>
      {/* <span style={{ color: 'blue', cursor: 'pointer' }} onClick={handleViewAllClick}>
        View All →
      </span> */}
      <div ref={chartRef} style={{ height: "305px" }}></div>
      {showIframe && (
        <Modal show={showIframe} onHide={handleCloseIframe} size="xl" centered>
          <Modal.Header closeButton>
            <Modal.Title>Analytics Dashboard</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ height: "80vh" }}>
            <iframe
              src={process.env.NX_APP_ANALYTICS ?? ""}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                borderRadius: "8px",
              }}
              title="Analytics Dashboard"
            />
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};
